import React, { useState, useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import createCashierApi from '../../../services/cashier/createCashier.js';
import { toast } from 'react-toastify';
import CASHIER_TOASTS from '../../../utils/constants/toastMesages/cashierToastMessage.js';

/**
 * Modal component for adding a new cashier.
 *
 * This component provides a form inside a modal where the admin can input
 * cashier details like name, username, and password. It includes basic validation
 * for required fields and password length, and uses an API call to submit the data.
 *
 * Props:
 * @param {Object} props - The props passed to this component
 * @param {boolean} props.show - Controls whether the modal is visible
 * @param {Function} props.onHide - Function to hide/close the modal
 *
 * State:
 * @state {Object} cashierData - Object storing input values (name, username, password)
 * @state {string} passwordError - String storing validation error message for password
 *
 * Example usage:
 * <AddCashierModal show={modalShow} onHide={() => setModalShow(false)} />
 *
 * @component
 * @returns {JSX.Element} Modal with form to add cashier
 *
 * @author Susovan
 */
function AddCashierModal(props) {

  //States for storing incoming cashier data from api
  const [cashierData, setCashierData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    role: "cashier",
    active: true,
  });

  //State for saving password error text
  const [passwordError, setPasswordError] = useState("");

  //Function for tracking changes in inputs and validating and shwing error
  const handleChange = (e) => {
    const { name, value } = e.target;

    setCashierData({
      ...cashierData,
      [name]: value,
    });

    if (name === "password") {
      if (value.length < 6) {
        setPasswordError("Password must be at least 6 characters");
      } else {
        setPasswordError("");
      }
    }
  };

  //Function for from validation
  const validateForm = () => {
    if (
      cashierData.name.trim() === "" ||
      cashierData.username.trim() === "" ||
      cashierData.password.trim() === ""
    ) {
      toast.error("All fields are required!");
      return false;
    }

    if (cashierData.password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }

    return true;
  };


  //Handler function for sending input data to backend
  const CreateCashierHandler = async () => {
    if (!validateForm()) return;

    try {
      // Append "_orderly" to username only if it's not already added
      const usernameWithSuffix = cashierData.username.endsWith('_orderly')
        ? cashierData.username
        : `${cashierData.username}_orderly`;

      const cashierToSend = {
        ...cashierData,
        username: usernameWithSuffix,
      };

      await createCashierApi(cashierToSend);
      toast.success(CASHIER_TOASTS.CASHIER_ADDED);
      setCashierData([]);
      props.onHide();
    } catch (error) {
      toast.error(CASHIER_TOASTS.CASHIER_NOT_ADDED);
    }
  };
  
  return (
    <Modal show={props.show} onHide={props.onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Cashier</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Cashier Name Input Field */}
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={cashierData.name}
              onChange={handleChange}
              placeholder="Enter name"
              onKeyDown={(e) => {if (e.key === "Enter") CreateCashierHandler()}}
            />
            {!cashierData.name &&
              <Form.Text className='text-muted'>
                Cashier name is required!*
              </Form.Text>
            }
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="text"
              name="email"
              value={cashierData.email}
              onChange={handleChange}
              placeholder="Enter cashier's email"
              onKeyDown={(e) => {if (e.key === "Enter") CreateCashierHandler()}}
            />
            {!cashierData.name &&
              <Form.Text className='text-muted'>
                Cashier email is required!*
              </Form.Text>
            }
          </Form.Group>
          {/* Username Input Field */}
          <Form.Group className="mb-3">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              name="username"
              value={cashierData.username}
              onChange={handleChange}
              placeholder="Enter username"
              onKeyDown={(e) => {if (e.key === "Enter") CreateCashierHandler()}}
            />
            <span className='d-flex flex-column'>
            {!cashierData.username &&
              <Form.Text className='text-muted'>
                Cashier uername is required!*
              </Form.Text>
            }
            <Form.Text className="text-muted">
              "_orderly" will be added automatically
            </Form.Text>
            </span>
          </Form.Group>

          {/* Password Input Field */}
          <Form.Group className="mb-2">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={cashierData.password}
              onChange={handleChange}
              placeholder="Enter password"
              isInvalid={passwordError !== ""}
              onKeyDown={(e) => {if (e.key === "Enter") CreateCashierHandler()}}
            />
            {passwordError && (
              <Form.Text className="text-danger">{passwordError}</Form.Text>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={props.onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={CreateCashierHandler}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddCashierModal;
